<template>
    <mobile-template title="Stock">

        <template #header-left>
            <div>
                <p class="text-xl font-bold text-gray-900">Stock</p>
                <p class="text-xs text-gray-400">Gestion des pièces</p>
            </div>
        </template>

        <!-- ══ BARRE SOMBRE CONTEXTE (cohérente avec les autres pages) ══ -->
        <div class="bg-gray-950 select-none">

            <!-- Étape 1 : titre simple -->
            <div v-if="step === 'action'" class="px-4 pt-4 pb-4">
                <p class="text-2xl font-extrabold text-white">Que souhaitez-vous faire ?</p>
                <p class="text-sm text-white/40 mt-1">Choisissez une action pour commencer</p>
            </div>

            <!-- Étapes suivantes : fil d'ariane + progression + bouton Terminer -->
            <div v-else class="px-4 pt-4 pb-3">
                <div class="flex items-center justify-between gap-3 mb-3">
                    <div class="flex items-center gap-2">
                        <button
                            class="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 active:bg-white/20"
                            @click="goBack"
                        >
                            <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <div>
                            <p class="text-base font-extrabold text-white leading-tight">{{ stepTitle }}</p>
                            <p class="text-xs text-white/40 mt-0.5">{{ stepSubtitle }}</p>
                        </div>
                    </div>
                    <!-- Bouton Terminer (visible sur l'étape pièces) -->
                    <button
                        v-if="step === 'parts'"
                        class="flex items-center gap-1.5 px-3 py-2 bg-white/10 active:bg-white/20 rounded-xl text-white text-xs font-bold transition-colors"
                        @click="resetToStart"
                    >
                        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Terminer
                    </button>
                </div>
                <!-- Progress bar dans le header sombre -->
                <div class="bg-white/10 rounded-full h-1.5 overflow-hidden">
                    <div class="h-1.5 bg-orange-500 rounded-full transition-all duration-300" :style="`width: ${stepProgress}%`"></div>
                </div>
            </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             ÉTAPE 1 — QUE SOUHAITEZ-VOUS FAIRE ?
        ══════════════════════════════════════════════════════════ -->
        <div v-if="step === 'action'" class="p-5 space-y-3">

            <!-- Charger -->
            <button
                class="w-full bg-white border-2 border-gray-200 rounded-3xl p-5 text-left active:border-emerald-400 active:bg-emerald-50 transition-all active:scale-98 group"
                @click="selectAction('charger')"
            >
                <div class="flex items-center gap-4">
                    <div class="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center shrink-0">
                        <svg class="w-7 h-7 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-base font-extrabold text-gray-900">Je charge mon camion</p>
                        <p class="text-sm text-gray-500 mt-0.5 leading-snug">Je pars en intervention et je prends des pièces du dépôt</p>
                    </div>
                    <svg class="w-5 h-5 text-gray-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </button>

            <!-- Décharger -->
            <button
                class="w-full bg-white border-2 border-gray-200 rounded-3xl p-5 text-left active:border-sky-400 active:bg-sky-50 transition-all active:scale-98 group"
                @click="selectAction('decharger')"
            >
                <div class="flex items-center gap-4">
                    <div class="w-14 h-14 rounded-2xl bg-sky-100 flex items-center justify-center shrink-0">
                        <svg class="w-7 h-7 text-sky-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-base font-extrabold text-gray-900">Je décharge mon camion</p>
                        <p class="text-sm text-gray-500 mt-0.5 leading-snug">Je rentre d'intervention, je rends ou j'ai utilisé des pièces</p>
                    </div>
                    <svg class="w-5 h-5 text-gray-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </button>

            <!-- Séparateur -->
            <div class="flex items-center gap-3 py-1">
                <div class="flex-1 h-px bg-gray-100"></div>
                <span class="text-xs text-gray-300 font-medium">ou</span>
                <div class="flex-1 h-px bg-gray-100"></div>
            </div>

            <!-- Consommer sur chantier -->
            <button
                class="w-full bg-gray-50 border border-gray-200 rounded-2xl p-4 text-left active:bg-orange-50 active:border-orange-200 transition-all"
                @click="selectAction('consommer')"
            >
                <div class="flex items-center gap-3">
                    <div class="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                        <svg class="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <div class="flex-1">
                        <p class="text-sm font-bold text-gray-800">Je suis sur chantier</p>
                        <p class="text-xs text-gray-400 mt-0.5">Je consomme une pièce de mon camion sur place</p>
                    </div>
                    <svg class="w-4 h-4 text-gray-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </button>

            <!-- Réceptionner -->
            <button
                class="w-full bg-gray-50 border border-gray-200 rounded-2xl p-4 text-left active:bg-gray-100 transition-all"
                @click="selectAction('reception')"
            >
                <div class="flex items-center gap-3">
                    <div class="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                        <svg class="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 5v14M5 12h14" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="flex-1">
                        <p class="text-sm font-bold text-gray-800">Réceptionner des pièces</p>
                        <p class="text-xs text-gray-400 mt-0.5">Enregistrer une livraison fournisseur</p>
                    </div>
                    <svg class="w-4 h-4 text-gray-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </button>

        </div>

        <!-- ══════════════════════════════════════════════════════════
             ÉTAPE 2 — SÉLECTION EMPLACEMENTS
        ══════════════════════════════════════════════════════════ -->
        <div v-else-if="step === 'locations' || step === 'reception-location'" class="p-5 space-y-6">

            <div v-if="loadingLocations" class="flex flex-col items-center py-20 gap-3">
                <div class="w-8 h-8 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div>
                <p class="text-sm text-gray-400">Chargement…</p>
            </div>

            <template v-else>

                <!-- Choisir le camion (charger/décharger) ou l'emplacement (réception) -->
                <div>
                    <p class="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">
                        {{ step === 'reception-location' ? 'Emplacement de destination' : 'Votre camion' }}
                    </p>
                    <p class="text-xs text-gray-400 mb-3">
                        {{ step === 'reception-location' ? 'Où livrer les pièces reçues ?' : 'Quel est votre véhicule ?' }}
                    </p>
                    <div class="grid grid-cols-2 gap-2">
                        <button
                            v-for="loc in (step === 'reception-location' ? locations : trucks)"
                            :key="loc.id"
                            class="flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 transition-all active:scale-95"
                            :class="selectedTruckId === loc.id
                                ? 'bg-gray-900 border-gray-900 text-white'
                                : 'bg-white border-gray-200 text-gray-700'"
                            @click="selectedTruckId = loc.id"
                        >
                            <i class="fas text-2xl" :class="[locationIcons[loc.type], selectedTruckId === loc.id ? 'text-white' : 'text-gray-400']"></i>
                            <span class="text-sm font-extrabold text-center leading-tight">{{ loc.name }}</span>
                        </button>
                    </div>
                    <p v-if="step !== 'reception-location' && trucks.length === 0" class="text-sm text-gray-400 text-center py-6">Aucun camion configuré</p>
                </div>

                <!-- Choisir l'autre emplacement (charger/décharger uniquement) -->
                <div v-if="step === 'locations' && action !== 'consommer'">
                    <p class="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">
                        {{ action === 'charger' ? 'Source des pièces' : 'Destination des pièces' }}
                    </p>
                    <p class="text-xs text-gray-400 mb-3">
                        {{ action === 'charger' ? 'Depuis quel dépôt chargez-vous ?' : 'Où remettez-vous les pièces ?' }}
                    </p>
                    <div class="grid grid-cols-2 gap-2">
                        <button
                            v-for="loc in otherLocations"
                            :key="loc.id"
                            class="flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 transition-all active:scale-95"
                            :class="selectedOtherId === loc.id
                                ? 'bg-orange-500 border-orange-500 text-white'
                                : 'bg-white border-gray-200 text-gray-700'"
                            @click="selectedOtherId = loc.id"
                        >
                            <i class="fas text-2xl" :class="[locationIcons[loc.type], selectedOtherId === loc.id ? 'text-white' : 'text-gray-400']"></i>
                            <span class="text-sm font-extrabold text-center leading-tight">{{ loc.name }}</span>
                        </button>
                    </div>
                </div>

                <!-- CTA -->
                <button
                    class="w-full py-4 bg-gray-900 text-white font-extrabold text-base rounded-2xl flex items-center justify-center gap-2 disabled:opacity-30 active:bg-black transition-all"
                    :disabled="!canProceedFromLocations"
                    @click="goToStep('parts')"
                >
                    {{ action === 'consommer' ? 'Voir les pièces de mon camion' : 'Voir les pièces disponibles' }}
                    <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

            </template>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             ÉTAPE 3 — LISTE DES PIÈCES
        ══════════════════════════════════════════════════════════ -->
        <div v-else-if="step === 'parts'">

            <!-- Bandeau contexte -->
            <div class="mx-4 mt-4 mb-3 flex items-center gap-2 px-4 py-2.5 bg-gray-900 rounded-2xl">
                <i class="fas text-sm text-white/50" :class="locationIcons[sourceLocation?.type]"></i>
                <span class="text-xs text-white/70 font-semibold">{{ sourceLocation?.name }}</span>
                <svg class="w-4 h-4 text-orange-400 shrink-0 mx-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <i class="fas text-sm text-white/50" :class="locationIcons[destLocation?.type]"></i>
                <span class="text-xs text-white/70 font-semibold">{{ destLocation?.name }}</span>
            </div>

            <!-- Recherche -->
            <div class="px-4 mb-3">
                <div class="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-3 py-2.5 shadow-sm">
                    <svg class="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" stroke-linecap="round"/>
                    </svg>
                    <input v-model="search" class="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-gray-400" placeholder="Rechercher une pièce…" @input="debouncedLoad"/>
                    <button v-if="search" class="text-gray-400" @click="search = ''; loadStock()">
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/></svg>
                    </button>
                </div>
            </div>

            <!-- Loader -->
            <div v-if="loading" class="flex flex-col items-center justify-center py-20 gap-3">
                <div class="w-8 h-8 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div>
                <p class="text-sm text-gray-400">Chargement…</p>
            </div>

            <!-- Vide -->
            <div v-else-if="rows.length === 0" class="flex flex-col items-center justify-center py-16 text-center px-8">
                <div class="w-16 h-16 bg-gray-100 rounded-3xl flex items-center justify-center mb-4">
                    <svg class="w-8 h-8 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>
                    </svg>
                </div>
                <p class="font-bold text-gray-600">Aucune pièce disponible</p>
                <p class="text-sm text-gray-400 mt-1">{{ search ? 'Aucun résultat' : 'Cet emplacement est vide' }}</p>
            </div>

            <!-- Liste -->
            <div v-else class="px-4 space-y-2 pb-8">
                <button
                    v-for="row in rows"
                    :key="`${row.product_id}-${row.location_id}`"
                    class="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 active:scale-98 active:bg-gray-50 transition-all text-left"
                    :class="row.quantity <= 0 ? 'opacity-50' : ''"
                    @click="openPartSheet(row)"
                >
                    <div
                        class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 font-black text-lg"
                        :class="row.quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'"
                    >
                        {{ row.quantity }}
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="font-bold text-gray-900 truncate">{{ row.product_name }}</p>
                        <p class="text-xs text-gray-400 font-mono mt-0.5">{{ row.reference || '—' }}</p>
                    </div>
                    <svg class="w-5 h-5 text-gray-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <!-- Pagination -->
                <div v-if="pagination.lastPage > 1" class="flex items-center justify-center gap-3 pt-2">
                    <button class="px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-bold text-gray-600 disabled:opacity-40" :disabled="pagination.currentPage <= 1" @click="changePage(pagination.currentPage - 1)">← Préc.</button>
                    <span class="text-sm text-gray-400 font-medium">{{ pagination.currentPage }} / {{ pagination.lastPage }}</span>
                    <button class="px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-bold text-gray-600 disabled:opacity-40" :disabled="pagination.currentPage >= pagination.lastPage" @click="changePage(pagination.currentPage + 1)">Suiv. →</button>
                </div>
            </div>

        </div>

        <!-- ══════════════════════════════════════════════════════════
             BOTTOM SHEET — ACTION SUR UNE PIÈCE
        ══════════════════════════════════════════════════════════ -->
        <transition name="sheet">
            <div v-if="partSheet.open" class="fixed inset-0 z-50 flex items-end bg-black/50" @click.self="partSheet.open = false">
                <div class="w-full bg-white rounded-t-3xl">

                    <div class="pt-3 pb-1 flex justify-center">
                        <div class="w-10 h-1 bg-gray-200 rounded-full"></div>
                    </div>

                    <!-- Info pièce -->
                    <div class="px-5 py-4 flex items-center gap-3 border-b border-gray-100">
                        <div
                            class="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 font-black text-2xl"
                            :class="partSheet.row?.quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'"
                        >
                            {{ partSheet.row?.quantity }}
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="font-extrabold text-gray-900 text-base leading-snug">{{ partSheet.row?.product_name }}</p>
                            <p class="text-xs text-gray-400 font-mono mt-0.5">{{ partSheet.row?.reference || '—' }}</p>
                        </div>
                    </div>

                    <!-- Quantité -->
                    <div class="px-5 py-4 border-b border-gray-100">
                        <p class="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Combien de pièces ?</p>
                        <div class="flex items-center gap-3">
                            <button class="w-14 h-14 rounded-2xl bg-gray-100 text-gray-700 font-black text-2xl flex items-center justify-center active:bg-gray-200 active:scale-95 transition-all" @click="partSheet.quantity = Math.max(1, partSheet.quantity - 1)">−</button>
                            <input v-model.number="partSheet.quantity" type="number" min="1" class="flex-1 text-center text-3xl font-black text-gray-900 border-2 border-gray-200 rounded-2xl py-3 outline-none focus:border-orange-400"/>
                            <button class="w-14 h-14 rounded-2xl bg-gray-100 text-gray-700 font-black text-2xl flex items-center justify-center active:bg-gray-200 active:scale-95 transition-all" @click="partSheet.quantity++">+</button>
                        </div>
                    </div>

                    <!-- Commentaire -->
                    <div class="px-5 pt-4 pb-2">
                        <input v-model="partSheet.comment" type="text" placeholder="Commentaire (optionnel)" class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 placeholder-gray-400"/>
                    </div>

                    <!-- Erreur -->
                    <div v-if="partSheet.error" class="mx-5 mt-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium">
                        {{ partSheet.error }}
                    </div>

                    <!-- Actions -->
                    <div class="px-4 pt-4 pb-8 space-y-2">

                        <!-- CHARGER : une seule action -->
                        <button
                            v-if="action === 'charger'"
                            class="w-full flex items-center gap-3 py-4 px-5 bg-emerald-500 text-white rounded-2xl font-extrabold text-sm active:bg-emerald-600 disabled:opacity-40 active:scale-98 transition-all"
                            :disabled="partSheet.loading || partSheet.row?.quantity <= 0"
                            @click="submitTransfer"
                        >
                            <span v-if="partSheet.loading" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin shrink-0"></span>
                            <svg v-else class="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <div class="flex-1 text-left">
                                <p>Je prends cette pièce avec moi</p>
                                <p class="text-xs font-medium text-white/70 mt-0.5">{{ sourceLocation?.name }} → {{ destLocation?.name }}</p>
                            </div>
                        </button>

                        <!-- DÉCHARGER : deux actions -->
                        <template v-if="action === 'decharger'">
                            <button
                                class="w-full flex items-center gap-3 py-4 px-5 bg-sky-500 text-white rounded-2xl font-extrabold text-sm active:bg-sky-600 disabled:opacity-40 active:scale-98 transition-all"
                                :disabled="partSheet.loading || partSheet.row?.quantity <= 0"
                                @click="submitTransfer"
                            >
                                <span v-if="partSheet.loading && partSheet.lastAction === 'transfer'" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin shrink-0"></span>
                                <svg v-else class="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M19 12H5M12 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <div class="flex-1 text-left">
                                    <p>Je pose cette pièce</p>
                                    <p class="text-xs font-medium text-white/70 mt-0.5">{{ sourceLocation?.name }} → {{ destLocation?.name }}</p>
                                </div>
                            </button>
                            <button
                                class="w-full flex items-center gap-3 py-4 px-5 bg-orange-500 text-white rounded-2xl font-extrabold text-sm active:bg-orange-600 disabled:opacity-40 active:scale-98 transition-all"
                                :disabled="partSheet.loading || partSheet.row?.quantity <= 0"
                                @click="submitConsume"
                            >
                                <span v-if="partSheet.loading && partSheet.lastAction === 'consume'" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin shrink-0"></span>
                                <svg v-else class="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="12" cy="12" r="10"/><path d="M8 12h8M12 8v8" stroke-linecap="round"/>
                                </svg>
                                <div class="flex-1 text-left">
                                    <p>J'ai utilisé cette pièce</p>
                                    <p class="text-xs font-medium text-white/70 mt-0.5">La pièce a été consommée lors de l'intervention</p>
                                </div>
                            </button>
                        </template>

                        <!-- CONSOMMER SUR CHANTIER : une seule action -->
                        <button
                            v-if="action === 'consommer'"
                            class="w-full flex items-center gap-3 py-4 px-5 bg-orange-500 text-white rounded-2xl font-extrabold text-sm active:bg-orange-600 disabled:opacity-40 active:scale-98 transition-all"
                            :disabled="partSheet.loading || partSheet.row?.quantity <= 0"
                            @click="submitConsume"
                        >
                            <span v-if="partSheet.loading" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin shrink-0"></span>
                            <svg v-else class="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <div class="flex-1 text-left">
                                <p>J'ai utilisé cette pièce</p>
                                <p class="text-xs font-medium text-white/70 mt-0.5">Consommée depuis {{ sourceLocation?.name }}</p>
                            </div>
                        </button>

                    </div>
                </div>
            </div>
        </transition>

    </mobile-template>
</template>

<script>
export default {
    name: 'StockMobile',

    data() {
        return {
            step:   'action',
            action: null,

            selectedTruckId: null,
            selectedOtherId: null,

            locations:  [],
            products:   [],
            rows:       [],
            pagination: { currentPage: 1, lastPage: 1 },
            search:     '',

            loadingLocations: false,
            loading:          false,
            _debounceTimer:   null,

            locationIcons: {
                warehouse: 'fa-warehouse',
                truck:     'fa-truck',
                site:      'fa-hard-hat',
            },

            partSheet: {
                open:       false,
                row:        null,
                quantity:   1,
                comment:    '',
                loading:    false,
                lastAction: null,
                error:      '',
            },
        };
    },

    computed: {
        stepTitle() {
            return {
                action:              'Stock',
                locations:           this.action === 'charger' ? 'Chargement' : this.action === 'consommer' ? 'Sur chantier' : 'Déchargement',
                'reception-location':'Réception',
                parts:               this.action === 'consommer' ? 'Consommer une pièce' : 'Choisir les pièces',
            }[this.step] || 'Stock';
        },

        stepSubtitle() {
            return {
                action:              'Que souhaitez-vous faire ?',
                locations:           this.action === 'consommer' ? 'Quel camion utilisez-vous ?' : 'Sélectionnez les emplacements',
                'reception-location':'Sélectionnez la destination',
                parts:               this.action === 'consommer'
                    ? `Pièces disponibles dans ${this.sourceLocation?.name || ''}`
                    : `${this.sourceLocation?.name || ''} → ${this.destLocation?.name || ''}`,
            }[this.step] || '';
        },

        stepProgress() {
            return { locations: 40, 'reception-location': 40, parts: 80 }[this.step] || 0;
        },

        trucks() {
            return this.locations.filter(l => l.type === 'truck');
        },

        otherLocations() {
            return this.locations.filter(l => l.id !== this.selectedTruckId);
        },

        sourceLocation() {
            if (this.action === 'charger')   return this.locations.find(l => l.id === this.selectedOtherId) || null;
            if (this.action === 'decharger') return this.locations.find(l => l.id === this.selectedTruckId) || null;
            if (this.action === 'consommer') return this.locations.find(l => l.id === this.selectedTruckId) || null;
            return null;
        },

        destLocation() {
            if (this.action === 'charger')   return this.locations.find(l => l.id === this.selectedTruckId) || null;
            if (this.action === 'decharger') return this.locations.find(l => l.id === this.selectedOtherId) || null;
            if (this.action === 'reception') return this.locations.find(l => l.id === this.selectedTruckId) || null;
            return null;
        },

        canProceedFromLocations() {
            if (this.step === 'reception-location') return !!this.selectedTruckId;
            if (this.action === 'consommer')        return !!this.selectedTruckId;
            return !!this.selectedTruckId && !!this.selectedOtherId;
        },
    },

    created() {
        this.loadLocations();
    },

    methods: {
        async loadLocations() {
            this.loadingLocations = true;
            try {
                const res = await this.$request.get('/stock/locations', {});
                this.locations = res?.locations || [];
                // Auto-sélection si 1 seul camion
                if (this.trucks.length === 1) this.selectedTruckId = this.trucks[0].id;
            } catch {}
            finally { this.loadingLocations = false; }
        },

        async loadStock(page = 1) {
            const sourceId = this.sourceLocation?.id;
            if (!sourceId) return;
            this.loading = true;
            try {
                const res = await this.$request.get('/stock/overview', {
                    location_id: sourceId,
                    search:      this.search || undefined,
                    page,
                });
                this.rows       = res?.rows?.data || [];
                this.pagination = { currentPage: res?.rows?.current_page || 1, lastPage: res?.rows?.last_page || 1 };
            } catch (e) {
                this.$notify?.error(e?.message || 'Impossible de charger les pièces');
            } finally {
                this.loading = false;
            }
        },

        debouncedLoad() {
            clearTimeout(this._debounceTimer);
            this._debounceTimer = setTimeout(() => this.loadStock(), 300);
        },

        changePage(page) { this.loadStock(page); },

        selectAction(action) {
            this.action          = action;
            this.selectedTruckId = null;
            this.selectedOtherId = null;
            this.step = action === 'reception' ? 'reception-location' : 'locations';
            // Auto-sélection camion unique
            if (this.trucks.length === 1) this.selectedTruckId = this.trucks[0].id;
            // Auto-sélection autre emplacement unique (pas pour consommer)
            if (action !== 'reception' && action !== 'consommer' && this.otherLocations.length === 1) {
                this.selectedOtherId = this.otherLocations[0].id;
            }
        },

        resetToStart() {
            this.step            = 'action';
            this.action          = null;
            this.selectedTruckId = null;
            this.selectedOtherId = null;
            this.rows            = [];
            this.search          = '';
        },

        goToStep(step) {
            this.step   = step;
            this.search = '';
            this.rows   = [];
            this.loadStock();
        },

        goBack() {
            if (this.step === 'parts') {
                this.step = this.action === 'reception' ? 'reception-location' : 'locations';
            } else {
                this.step   = 'action';
                this.action = null;
                this.selectedTruckId = null;
                this.selectedOtherId = null;
            }
        },

        openPartSheet(row) {
            this.partSheet = { open: true, row, quantity: 1, comment: '', loading: false, lastAction: null, error: '' };
        },

        async submitTransfer() {
            const { row, quantity, comment } = this.partSheet;
            this.partSheet.loading    = true;
            this.partSheet.lastAction = 'transfer';
            this.partSheet.error      = '';
            try {
                await this.$request.post('/stock/transfer', {
                    product_id:          row.product_id,
                    from_location_token: this.sourceLocation?.token,
                    to_location_token:   this.destLocation?.token,
                    quantity,
                    comment: comment || undefined,
                });
                this.partSheet.open = false;
                await this.loadStock();
                this.$notify?.success(this.action === 'charger'
                    ? `Chargé dans ${this.destLocation?.name} ✓`
                    : `Rendu à ${this.destLocation?.name} ✓`);
            } catch (e) {
                this.partSheet.error = e?.message || 'Une erreur est survenue';
            } finally {
                this.partSheet.loading = false;
            }
        },

        async submitConsume() {
            const { row, quantity, comment } = this.partSheet;
            this.partSheet.loading    = true;
            this.partSheet.lastAction = 'consume';
            this.partSheet.error      = '';
            try {
                await this.$request.post('/stock/consume', {
                    location_token: this.sourceLocation?.token,
                    product_id:     row.product_id,
                    quantity,
                    comment: comment || undefined,
                });
                this.partSheet.open = false;
                await this.loadStock();
                this.$notify?.success('Consommation enregistrée ✓');
            } catch (e) {
                this.partSheet.error = e?.message || 'Une erreur est survenue';
            } finally {
                this.partSheet.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.sheet-enter-active { transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
.sheet-leave-active { transition: all 0.2s ease-in; }
.sheet-enter-from, .sheet-leave-to { opacity: 0; transform: translateY(100%); }
</style>